import React, { useState, useEffect, useCallback, useRef } from 'react';
import API from '../api';
import PoolSelector from '../components/PoolSelector';
import NoPoolYet from '../components/NoPoolYet';
import UserAvatar from '../components/UserAvatar';
import Podium from '../components/Podium';
import PushNotify from '../components/PushNotify';
import PickReveal from '../components/PickReveal';

const s = {
  page: { padding: '24px 20px', maxWidth: 900, margin: '0 auto' },
  header: { marginBottom: 28 },
  weekBadge: {
    display: 'inline-block',
    background: 'linear-gradient(135deg, #013369, #0a5299)',
    padding: '6px 20px',
    borderRadius: 20,
    fontSize: 13,
    fontWeight: 700,
    color: '#7fb3ff',
    marginBottom: 12,
    letterSpacing: 1
  },
  title: {
    fontFamily: "'Oswald', sans-serif",
    fontSize: 28,
    fontWeight: 700,
    color: '#fff',
    marginBottom: 6
  },
  subtitle: { color: '#667', fontSize: 14 },
  stats: {
    display: 'flex',
    gap: 12,
    marginBottom: 28,
    flexWrap: 'wrap'
  },
  statCard: {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 10,
    padding: '14px 20px',
    flex: '1 1 140px'
  },
  statValue: {
    fontFamily: "'Oswald', sans-serif",
    fontSize: 28,
    fontWeight: 700,
    color: '#7fb3ff'
  },
  statLabel: { fontSize: 12, color: '#667', marginTop: 2 },
  table: {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 12,
    overflow: 'hidden'
  },
  tableHead: {
    display: 'grid',
    gridTemplateColumns: 'var(--lb-cols, 50px minmax(0,1fr) 80px 80px 80px 120px)',
    padding: '12px 20px',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    background: 'rgba(255,255,255,0.04)'
  },
  tableHeadCell: { fontSize: 11, fontWeight: 700, color: '#667', letterSpacing: 1, textTransform: 'uppercase' },
  tableRow: {
    display: 'grid',
    gridTemplateColumns: 'var(--lb-cols, 50px minmax(0,1fr) 80px 80px 80px 120px)',
    padding: '14px 20px',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
    alignItems: 'center',
    transition: 'background 0.15s'
  },
  tableRowTop: {
    background: 'linear-gradient(90deg, rgba(213,10,10,0.1), transparent)',
    borderLeft: '3px solid #D50A0A'
  },
  tableRowMe: {
    background: 'linear-gradient(90deg, rgba(1,51,105,0.2), transparent)',
    borderLeft: '3px solid #013369'
  },
  rank: {
    fontFamily: "'Oswald', sans-serif",
    fontSize: 18,
    fontWeight: 700,
    color: '#445'
  },
  rank1: { color: '#FFD700' },
  rank2: { color: '#C0C0C0' },
  rank3: { color: '#CD7F32' },
  username: { fontSize: 15, fontWeight: 600, color: '#dde' },
  usernameMe: { color: '#7fb3ff' },
  correctCell: { fontSize: 15, fontWeight: 700, color: '#4caf50', textAlign: 'center' },
  wrongCell: { fontSize: 15, fontWeight: 700, color: '#f44336', textAlign: 'center' },
  pendingCell: { fontSize: 13, color: '#889', textAlign: 'center' },
  tbCell: { fontSize: 12, color: '#889' },
  liveChip: {
    display: 'inline-block',
    background: 'rgba(255,165,0,0.2)',
    color: '#ffa500',
    padding: '2px 8px',
    borderRadius: 10,
    fontSize: 10,
    fontWeight: 700,
    marginLeft: 6
  },
  refreshBtn: {
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.1)',
    color: '#aab',
    padding: '8px 16px',
    borderRadius: 8,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: 6
  },
  lastUpdated: { fontSize: 12, color: '#445', marginLeft: 12 },
  // Last-game contender banner
  contenderBanner: {
    background: 'linear-gradient(135deg, rgba(255,165,0,0.12), rgba(213,10,10,0.10))',
    border: '1px solid rgba(255,165,0,0.35)',
    borderRadius: 12,
    padding: '16px 20px',
    marginBottom: 24,
    display: 'flex',
    alignItems: 'flex-start',
    gap: 14
  },
  contenderIcon: { fontSize: 28, lineHeight: 1, flexShrink: 0 },
  contenderBody: { flex: 1 },
  contenderHeading: {
    fontFamily: "'Oswald', sans-serif",
    fontSize: 15,
    fontWeight: 700,
    color: '#ffa500',
    letterSpacing: 0.5,
    marginBottom: 6
  },
  contenderList: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  contenderChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 20,
    padding: '4px 12px',
    fontSize: 13,
    fontWeight: 600,
    color: '#dde'
  },
  contenderChipLeader: {
    background: 'rgba(255,215,0,0.12)',
    border: '1px solid rgba(255,215,0,0.4)',
    color: '#FFD700'
  },
  contenderSub: { fontSize: 12, color: '#889', marginTop: 8 },
  // Viewer "war room" projection banner
  projBanner: {
    borderRadius: 12,
    padding: '14px 18px',
    marginBottom: 20,
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    border: '1px solid'
  },
  projIcon: { fontSize: 26, lineHeight: 1, flexShrink: 0 },
  projText: { fontSize: 14, fontWeight: 600, lineHeight: 1.4 }
};

// Color/icon per projection state.
const PROJ_STYLE = {
  clinched:   { bg: 'rgba(255,215,0,0.10)',  border: 'rgba(255,215,0,0.4)',  color: '#FFD700', icon: '👑' },
  contention: { bg: 'rgba(127,179,255,0.08)', border: 'rgba(127,179,255,0.3)', color: '#7fb3ff', icon: '🎯' },
  soloLeader: { bg: 'rgba(127,179,255,0.08)', border: 'rgba(127,179,255,0.3)', color: '#7fb3ff', icon: '🎯' },
  locked:     { bg: 'rgba(255,255,255,0.04)', border: 'rgba(255,255,255,0.12)', color: '#aab',    icon: '🔒' },
  eliminated: { bg: 'rgba(244,67,54,0.08)',   border: 'rgba(244,67,54,0.3)',   color: '#f4978e', icon: '🪦' }
};

function getRankStyle(rank) {
  if (rank === 1) return s.rank1;
  if (rank === 2) return s.rank2;
  if (rank === 3) return s.rank3;
  return {};
}

function getRankEmoji(rank) {
  if (rank === 1) return '🥇';
  if (rank === 2) return '🥈';
  if (rank === 3) return '🥉';
  return rank;
}

/**
 * How to caption a tiebreaker prediction. Going over now costs you the tie
 * outright, so the board has to say which side of the total you landed on —
 * otherwise two players both "off 2" appear identical while ranking apart.
 * (`wentUnder` is 1 when the guess went OVER — see utils/standings.js.)
 */
function tbNote(entry) {
  if (entry.tbDiff == null) return '';
  if (entry.wentUnder === 1) return ` (${entry.tbDiff} over)`;
  return ` (off ${entry.tbDiff})`;
}

export default function LeaderboardPage({ user, pools, poolsLoaded, selectedPool, onSelectPool, onManagePools }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [allWeeks, setAllWeeks] = useState([]);
  const [selectedWeekId, setSelectedWeekId] = useState(null);
  // Keyed by entry, because form belongs to a pick sheet: someone holding two
  // entries can be running hot on one and cold on the other.
  const [streaks, setStreaks] = useState({});
  const streakOf = (row) => streaks[row.entry_id ?? row.user_id];
  const [showReveal, setShowReveal] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const prevPoolIdRef = useRef(undefined);
  // Every request takes a ticket. This board polls in the background, so when
  // you switch pools there is usually a request for the old pool still in the
  // air — and it used to land afterwards and paint the old pool's standings
  // back over the new ones, where they sat until the next 30s tick.
  const reqRef = useRef(0);

  const load = useCallback(async (weekId, poolId) => {
    const ticket = ++reqRef.current;
    // Only show spinner when pool/week actually changes, not on background refreshes
    const isFirstLoad = prevPoolIdRef.current === undefined;
    if (isFirstLoad || poolId !== prevPoolIdRef.current) {
      setLoading(true);
      prevPoolIdRef.current = poolId;
    }
    try {
      let url = '/picks/leaderboard';
      const params = [];
      if (weekId) params.push(`week_id=${weekId}`);
      if (poolId) params.push(`pool_id=${poolId}`);
      if (params.length) url += '?' + params.join('&');
      const res = await API.get(url);
      if (ticket !== reqRef.current) return;   // a newer pool/week won the race
      setData(res.data);
      setLastUpdated(new Date());
      // Load streaks for this pool
      if (poolId) {
        API.get('/picks/streaks?pool_id=' + poolId).then(sr => {
          if (ticket !== reqRef.current) return;
          const map = {};
          (sr.data.streaks || []).forEach(streak => { if (streak.streak > 0) map[streak.entry_id ?? streak.user_id] = streak; });
          setStreaks(map);
        }).catch(() => {});
      }
    } catch (err) {
      console.error(err);
    } finally {
      // Don't let a stale response clear the spinner the new load just raised.
      if (ticket === reqRef.current) setLoading(false);
    }
  }, []);

  // Week-by-week history for this pool: who won, and how you did.
  const histReqRef = useRef(0);
  const loadHistory = useCallback(async (pid) => {
    const ticket = ++histReqRef.current;
    if (!pid) { setHistory([]); return; }
    try {
      const res = await API.get(`/pools/${pid}/history`);
      if (ticket !== histReqRef.current) return;
      setHistory(res.data?.history || []);
    } catch (e) { if (ticket === histReqRef.current) setHistory([]); }
  }, []);

  const loadWeeks = useCallback(async () => {
    try {
      const res = await API.get('/picks/weeks');
      setAllWeeks(res.data.weeks || []);
    } catch (e) {}
  }, []);

  // Use pool ID (not object reference) and weekId as dependencies to avoid flicker
  const poolId = selectedPool?.id;
  // Poll faster (15s) while games are live for a real-time "war room" feel,
  // slower (30s) otherwise. Only the current week is live; past weeks are static.
  const liveNow = !selectedWeekId && (data?.leaderboard?.some(e => e.inProgress > 0) || false);
  // Hold the cadence in a ref so the polling loop can adapt without the effect
  // tearing down and recreating itself every time games go live/idle.
  const liveRef = useRef(false);
  liveRef.current = liveNow;
  useEffect(() => { loadHistory(poolId); }, [loadHistory, poolId]);

  useEffect(() => {
    loadWeeks();
    let timer;
    let cancelled = false;
    const cycle = async () => {
      await load(selectedWeekId, poolId);
      if (!cancelled) timer = setTimeout(cycle, liveRef.current ? 15000 : 30000);
    };
    cycle();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [load, loadWeeks, selectedWeekId, poolId]);

  const handleWeekChange = (weekId) => {
    setSelectedWeekId(weekId || null);
    setLoading(true);
  };

  // Before anything else, including the spinner: with no pool there is no board
  // to rank them on, and the ungated one is a list of strangers.
  if (poolsLoaded && (!pools || pools.length === 0)) return <NoPoolYet onGoToPools={onManagePools} />;

  if (loading) return (
    <div style={{ ...s.page, textAlign: 'center', paddingTop: 80 }}>
      <div style={{ fontSize: 40, marginBottom: 16 }}>🏆</div>
      <div style={{ color: '#667' }}>Loading leaderboard...</div>
    </div>
  );

  if (!data || !data.week) return (
    <div style={{ ...s.page, textAlign: 'center', paddingTop: 80 }}>
      <div style={{ fontSize: 40, marginBottom: 16 }}>🏆</div>
      <div style={{ color: '#667' }}>No active week yet. Check back once the schedule is loaded.</div>
    </div>
  );

  const { leaderboard, week, totalGames, completedGames, projection } = data;
  const unpaid = data.unpaid || [];
  const hasInProgress = leaderboard.some(e => e.inProgress > 0);
  const projStyle = projection ? (PROJ_STYLE[projection.state] || PROJ_STYLE.contention) : null;

  // ── Last-game contender logic ──
  //
  // The tiebreaker game scores like any other now, so it is simply the last game
  // of the week — no special case needed to notice it, and "1 remaining" covers
  // Monday night. What IS still special is its score prediction, which breaks a
  // tie, so when the last game is the tiebreaker the banner also shows the calls.
  const remainingGames = totalGames - completedGames;
  const showContenderBanner = remainingGames === 1 && leaderboard.length > 0;
  const tbGame = data?.tiebreakerGame || null;
  const lastGameIsTiebreaker = !!(showContenderBanner && tbGame && tbGame.status !== 'final');

  // The server decides who is still live, because it is the only side that can:
  // working it out needs everyone's pick on the last game, and those stay hidden
  // until kickoff. It also answers the right question — "best case reaches the
  // leader" wrongly counts a chaser who backed the SAME team as the leader, and
  // that game lifts them both, so the gap never closes.
  const contention = data?.contention || null;
  const leaderScore = leaderboard[0]?.correct ?? 0;
  const liveIds = contention ? new Set(contention.canWin) : null;
  const contenders = !showContenderBanner ? []
    : liveIds ? leaderboard.filter(e => liveIds.has(e.entry_id))
    : leaderboard.filter(e => e.correct + e.pending + e.inProgress >= leaderScore);

  // Calls listed low to high, anyone who didn't predict a score last.
  //
  // Deliberately NOT ranked, and no "currently winning" marker, because during
  // the game there is no such thing: closest-without-going-over is judged on the
  // FINAL total, so a call above the running score hasn't gone over — it just
  // hasn't been reached. Naming a leader there would put the wrong name on
  // screen at the loudest moment of the week. Calls the total has already passed
  // are dimmed instead, which is a fact rather than a guess.
  const tbCallers = !lastGameIsTiebreaker ? [] : [...contenders].sort((a, b) => {
    const aHas = a.tbPredicted != null, bHas = b.tbPredicted != null;
    if (aHas !== bHas) return aHas ? -1 : 1;
    if (!aHas) return 0;
    return a.tbPredicted - b.tbPredicted;
  });

  return (
    <div>
      <PoolSelector pools={pools} selectedPool={selectedPool} onSelect={onSelectPool} onManage={onManagePools} />
    <div style={s.page}>
      <div style={s.header}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
          <div>
            <div style={s.weekBadge}>🏆 {week.label}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={s.title}>Weekly Leaderboard</div>
              {hasInProgress && <span style={s.liveChip}>LIVE</span>}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span style={s.subtitle}>{liveNow ? 'Live, updating every 15s' : 'Updates automatically during games'}</span>
              <PushNotify />
              {poolId && (
                <button
                  onClick={() => setShowReveal(true)}
                  style={{ padding:'6px 14px', borderRadius:8, border:'1px solid rgba(127,179,255,0.3)', background:'rgba(127,179,255,0.08)', color:'#7fb3ff', cursor:'pointer', fontSize:12, fontWeight:700, letterSpacing:0.5 }}
                >
                  👁 View Picks
                </button>
              )}
            </div>
          </div>
          {allWeeks.length > 1 && (
            <div>
              <div style={{ fontSize: 11, color: '#667', marginBottom: 6, fontWeight: 600, letterSpacing: 0.5 }}>VIEW WEEK</div>
              <select
                style={{ padding: '8px 12px', background: '#0d1526', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8, color: '#fff', fontSize: 13, outline: 'none', cursor: 'pointer' }}
                value={selectedWeekId || ''}
                onChange={e => handleWeekChange(e.target.value)}
              >
                <option value="">Current Week</option>
                {allWeeks.map(w => (
                  <option key={w.id} value={w.id}>{w.label}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* ── Viewer "war room" projection ── */}
      {projection && projStyle && (
        <div style={{ ...s.projBanner, background: projStyle.bg, borderColor: projStyle.border }}>
          <div style={s.projIcon}>{projStyle.icon}</div>
          <div style={{ ...s.projText, color: projStyle.color }}>{projection.message}</div>
        </div>
      )}

      {/* ── Last-game contender banner ── */}
      {showContenderBanner && (
        <div style={s.contenderBanner}>
          <div style={s.contenderIcon}>🏁</div>
          <div style={s.contenderBody}>
            <div style={s.contenderHeading}>
              LAST GAME OF THE WEEK. STILL IN THE RUNNING
            </div>
            <div style={s.contenderList}>
              {contenders.map(e => {
                const isLeader = e.correct === leaderScore && e.rank === 1;
                const needsWin = leaderScore - e.correct; // how many they need to catch up
                return (
                  <span
                    key={e.entry_id ?? e.user_id}
                    style={{ ...s.contenderChip, ...(isLeader ? s.contenderChipLeader : {}) }}
                  >
                    <UserAvatar username={e.username} size={20} />
                    {e.username}
                    {needsWin === 0
                      ? <span style={{ opacity: 0.7, fontSize: 11 }}>({e.correct} pts)</span>
                      : <span style={{ opacity: 0.7, fontSize: 11 }}>(needs win)</span>
                    }
                  </span>
                );
              })}
            </div>
            <div style={s.contenderSub}>
              {contenders.length === 1
                ? `${contenders[0].username} leads and just needs to hold on.`
                : contention && contention.exact
                  ? `${contenders.length} can still win the week. It all comes down to this last game.`
                  : `${contenders.length} are within reach. Picks for the last game stay hidden until kickoff, so this list may narrow once they're out.`
              }
            </div>

            {/* The last game being the tiebreaker means two things at once: its
                winner is worth a point like any other, AND its combined score
                separates anyone who ends up level. Worth spelling out, because
                those are different questions and both are live right now. */}
            {lastGameIsTiebreaker && contenders.length > 1 && (
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                <div style={{ fontSize: 12, color: '#7fb3ff', fontWeight: 700, letterSpacing: 0.5, marginBottom: 8 }}>
                  {tbGame.away_abbr} @ {tbGame.home_abbr} · IF THEY FINISH LEVEL, THE SCORE DECIDES
                </div>
                <div style={s.contenderList}>
                  {tbCallers.map(e => {
                    // A call the running total has already passed can't come
                    // back — the gap only widens. That's a fact worth showing;
                    // who is "winning" mid-game is not, so it isn't shown.
                    const strandedUnder = tbGame.currentTotal != null &&
                                          e.tbPredicted != null &&
                                          e.tbPredicted < tbGame.currentTotal;
                    return (
                      <span key={'tb' + (e.entry_id ?? e.user_id)}
                            style={{ ...s.contenderChip, opacity: strandedUnder ? 0.5 : 1 }}>
                        {e.username}
                        <span style={{ opacity: 0.7, fontSize: 11 }}>
                          {e.tbPredicted != null ? `called ${e.tbPredicted}` : 'no call'}
                        </span>
                      </span>
                    );
                  })}
                </div>
                <div style={s.contenderSub}>
                  {tbGame.currentTotal != null
                    ? `Combined score is ${tbGame.currentTotal} so far. Closest without going over wins a tie.`
                    : 'Closest to the combined final score without going over wins a tie.'}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div style={s.stats}>
        <div style={s.statCard}>
          <div style={s.statValue}>{leaderboard.length}</div>
          <div style={s.statLabel}>Players in pool</div>
        </div>
        <div style={s.statCard}>
          <div style={s.statValue}>{completedGames}/{totalGames}</div>
          <div style={s.statLabel}>Games completed</div>
        </div>
        <div style={s.statCard}>
          <div style={s.statValue}>
            {leaderboard.find(e => e.username === user.username)?.correct ?? '-'}
          </div>
          <div style={s.statLabel}>Your correct picks</div>
        </div>
        <div style={s.statCard}>
          <div style={{ ...s.statValue, color: '#D50A0A' }}>
            #{leaderboard.find(e => e.username === user.username)?.rank ?? '-'}
          </div>
          <div style={s.statLabel}>Your rank</div>
        </div>
      </div>

      {/* Top three. Score in the box, place spelled out; see Podium.jsx. */}
      <Podium unit="correct"
        rows={leaderboard.slice(0, 3).map(e => ({ username: e.username, rank: e.rank, score: e.correct }))} />

      {/* The six fixed columns overflow a phone, pushing the tiebreaker — the
          number that settles a tied week — off the right edge. On narrow screens
          drop to four columns and move the tiebreaker under the player name. */}
      <style>{`
        .lb-table { --lb-cols: 50px minmax(0,1fr) 80px 80px 80px 120px; }
        .lb-tb-inline { display: none; }
        .lb-rank-short { display: none; }
        @media (max-width: 640px) {
          .lb-table { --lb-cols: 34px minmax(0,1fr) 40px 40px; }
          .lb-table .lb-col-pending,
          .lb-table .lb-col-tb { display: none; }
          .lb-tb-inline { display: block; font-size: 11px; color: #667; margin-top: 2px; }
          /* 20px of side padding on a 360px phone is 40px the record could
             have used. */
          .lb-table > div { padding-left: 12px; padding-right: 12px; }
          /* A streak chip and a LIVE chip are about 90px between them, which is
             most of the name column on a phone. Chips never shrink, so short
             names were being truncated to a single letter to make room for
             them. Let the line wrap instead: the name keeps the full width and
             the chips drop underneath. */
          .lb-name-line { flex-wrap: wrap; row-gap: 4px; }
          .lb-rank-long { display: none; }
          .lb-rank-short { display: inline; }
        }
      `}</style>
      <div style={s.table} className="lb-table">
        <div style={s.tableHead}>
          <div style={s.tableHeadCell}>
            <span className="lb-rank-long">Rank</span>
            <span className="lb-rank-short">#</span>
          </div>
          <div style={s.tableHeadCell}>Player</div>
          <div style={{ ...s.tableHeadCell, textAlign: 'center' }}>✅ W</div>
          <div style={{ ...s.tableHeadCell, textAlign: 'center' }}>❌ L</div>
          <div style={{ ...s.tableHeadCell, textAlign: 'center' }} className="lb-col-pending">⏳</div>
          <div style={s.tableHeadCell} className="lb-col-tb">Tiebreaker</div>
        </div>

        {leaderboard.map((entry) => {
          const isMe = entry.user_id != null ? entry.user_id === user.id : entry.username === user.username;
          const isTop = entry.rank === 1;
          return (
            <div
              key={entry.entry_id ?? entry.user_id}
              style={{
                ...s.tableRow,
                ...(isTop ? s.tableRowTop : {}),
                ...(isMe ? s.tableRowMe : {})
              }}
            >
              <div style={{ ...s.rank, ...getRankStyle(entry.rank) }}>
                {getRankEmoji(entry.rank)}
              </div>
              {/* A username can be a whole email address, and an email has no
                  place to break. Left to size itself the name track grew past
                  its share and shoved the W and L columns out of a card that
                  clips its overflow, so the record — the only reason to open
                  this screen — was gone. The name truncates now and the numbers
                  stay put. Chips never shrink; the name always can. */}
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                <UserAvatar username={entry.username} size={32} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="lb-name-line" style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                    <span title={entry.username}
                      style={{ ...s.username, ...(isMe ? s.usernameMe : {}), minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {entry.username}
                    </span>
                    {isMe && <span style={{ fontSize: 11, color: '#667', flexShrink: 0 }}>(you)</span>}
                    {streakOf(entry) && (
                      <span title={streakOf(entry).type === 'hot' ? 'Hot streak, top 20% for ' + streakOf(entry).streak + ' week(s)' : 'Cold streak, bottom 20% for ' + streakOf(entry).streak + ' week(s)'}
                        style={{ fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 2, padding: '1px 7px', borderRadius: 10, flexShrink: 0, background: streakOf(entry).type === 'hot' ? 'rgba(255,100,0,0.15)' : 'rgba(127,179,255,0.1)', border: '1px solid ' + (streakOf(entry).type === 'hot' ? 'rgba(255,100,0,0.3)' : 'rgba(127,179,255,0.2)') }}>
                        {streakOf(entry).type === 'hot' ? '🔥' : '🥶'}{streakOf(entry).streak}
                      </span>
                    )}
                    {/* The ⭐ and 🏆 badges lived here and are gone. On a phone the
                        name row already carries an avatar, the name, "(you)", a
                        streak chip and a LIVE chip — two more pills pushed it to
                        wrap and made the row hard to read, which is the opposite of
                        what a standings table is for. Badges are still earned, still
                        stored, and still shown on a player's own profile; they just
                        don't crowd the board. */}
                    {entry.inProgress > 0 && (
                      <span style={{ ...s.liveChip, flexShrink: 0, marginLeft: 0 }}>{entry.inProgress} LIVE</span>
                    )}
                  </div>
                  {/* Mobile only — the tiebreaker column is hidden at that
                      width. A block sibling puts it on its own line without
                      asking the row above it to wrap. */}
                  {entry.tbPredicted != null && (
                    <div className="lb-tb-inline">
                      TB {entry.tbPredicted}{tbNote(entry)}
                    </div>
                  )}
                </div>
              </div>
              <div style={s.correctCell}>{entry.correct}</div>
              <div style={s.wrongCell}>{entry.wrong}</div>
              <div style={s.pendingCell} className="lb-col-pending">{entry.pending > 0 ? entry.pending : '-'}</div>
              <div style={s.tbCell} className="lb-col-tb">
                {entry.tbPredicted != null ? (
                  <span>Pred: {entry.tbPredicted}<span style={{ color: entry.wentUnder === 1 ? '#c9803a' : '#445', marginLeft:4 }}>{tbNote(entry)}</span></span>
                ) : '-'}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Picked, but not paid ──
          Shown rather than hidden. Their picks are stored and graded; what they
          have lost is a position on the board, and somebody who simply vanished
          would ask why rather than pay. The record is left visible on purpose:
          it is the strongest argument for settling up. */}
      {unpaid.length > 0 && (
        <div style={{ marginTop: 22, background: 'rgba(255,255,255,0.02)', border: '1px dashed rgba(255,255,255,0.14)', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ padding: '11px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#ffa726', letterSpacing: 0.5 }}>NOT ON THE BOARD YET</span>
            <span style={{ fontSize: 12.5, color: '#667' }}>
              {unpaid.length === 1 ? 'One sheet is' : `${unpaid.length} sheets are`} still unpaid. Picks are saved and will count as soon as the season is paid for.
            </span>
          </div>
          {unpaid.map(entry => (
            <div key={entry.entry_id} style={{ display: 'grid', gridTemplateColumns: 'var(--lb-cols)', alignItems: 'center', padding: '10px 16px', borderTop: '1px solid rgba(255,255,255,0.04)', opacity: 0.72 }}>
              <div style={{ fontSize: 13, color: '#556', fontWeight: 700 }}>·</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                <UserAvatar username={entry.account_username || entry.username} size={32} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span title={entry.username} style={{ ...s.username, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#99a' }}>
                    {entry.username}
                  </span>
                </div>
              </div>
              <div style={{ ...s.correctCell, color: '#5c7a5e' }}>{entry.correct}</div>
              <div style={{ ...s.wrongCell, color: '#7a5c5c' }}>{entry.wrong}</div>
              <div style={s.pendingCell} className="lb-col-pending">{entry.pending > 0 ? entry.pending : '-'}</div>
              <div style={{ fontSize: 11.5, color: '#ffa726', fontWeight: 700 }} className="lb-col-tb">UNPAID</div>
            </div>
          ))}
        </div>
      )}

      {/* ── Week-by-week history: who won each week, and how you did ── */}
      {history.length > 0 && (
        <div style={{ marginTop: 28, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, overflow: 'hidden' }}>
          <button
            onClick={() => setShowHistory(v => !v)}
            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '14px 18px', background: 'rgba(255,255,255,0.04)', border: 'none', borderBottom: showHistory ? '1px solid rgba(255,255,255,0.08)' : 'none', cursor: 'pointer', textAlign: 'left' }}
          >
            <span style={{ fontFamily: "'Oswald', sans-serif", fontSize: 14, fontWeight: 700, color: '#dde', letterSpacing: 0.8, textTransform: 'uppercase' }}>
              📅 Week History
            </span>
            <span style={{ fontSize: 12, color: '#667' }}>
              {history.length} week{history.length === 1 ? '' : 's'} {showHistory ? '▲' : '▼'}
            </span>
          </button>

          {showHistory && history.map(h => {
            const isViewing = String(selectedWeekId || '') === String(h.week_id);
            const names = h.winners.map(w => w.username).join(' & ');
            const w0 = h.winners[0];
            return (
              <div
                key={h.week_id}
                onClick={() => handleWeekChange(String(h.week_id))}
                title="View this week's full standings"
                style={{
                  display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
                  padding: '12px 18px', cursor: 'pointer',
                  borderBottom: '1px solid rgba(255,255,255,0.05)',
                  background: isViewing ? 'rgba(127,179,255,0.08)' : 'transparent'
                }}
              >
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: isViewing ? '#7fb3ff' : '#dde' }}>
                    {h.label}
                    {isViewing && <span style={{ fontSize: 11, color: '#667', marginLeft: 6 }}>(viewing)</span>}
                  </div>
                  <div style={{ fontSize: 12, color: '#889', marginTop: 3 }}>
                    {h.tied ? '🤝 Tied: ' : '🥇 '}{names}
                    <span style={{ color: '#667' }}> · {w0.correct} correct</span>
                    {w0.tbDiff != null && (
                      <span style={{ color: '#667' }}>
                        {' · TB '}{w0.tbPredicted}{w0.wentOver ? ` (${w0.tbDiff} over)` : ` (off ${w0.tbDiff})`}
                      </span>
                    )}
                  </div>
                </div>
                {h.me ? (
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontFamily: "'Oswald', sans-serif", fontSize: 15, fontWeight: 700, color: h.me.rank === 1 ? '#FFD700' : '#aab' }}>
                      #{h.me.rank}
                    </div>
                    <div style={{ fontSize: 11.5, color: '#667' }}>
                      <span style={{ color: '#4caf50' }}>{h.me.correct}</span>
                      <span style={{ color: '#445' }}>-</span>
                      <span style={{ color: '#f44336' }}>{h.me.wrong}</span>
                    </div>
                  </div>
                ) : (
                  <div style={{ fontSize: 11.5, color: '#445', flexShrink: 0 }}>didn't play</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div style={{ height: 40 }} />
      {showReveal && poolId && (
        <PickReveal
          poolId={poolId}
          selectedWeekId={selectedWeekId}
          user={user}
          onClose={() => setShowReveal(false)}
        />
      )}
    </div>
  </div>
  );
}
